// Layer 3 (Transformations, pure): Advisory › Driver tree.
// Ranks Paul's six issues by US$/day EBITDA impact, derives counted exposure,
// breadcrumbs and persona projection. No React, no engine
// mutations, no side effects. Doc: docs/logic/advisory/driver-tree.md.

import type {
  DriverEdge,
  DriverIssue,
  DriverIssueImpactType,
  DriverIssueSeed,
  DriverIssueValueBasis,
  DriverNode,
  PricedDriverTree,
  DriverTreeSeed,
} from '@/versions/v1/schema/driver-tree';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { TrendReference, TrendSeries } from '@/versions/v1/modules/_viz';
import type { Persona } from '@/shared/types';
import { diffMinutes, formatDayMonth, formatElapsedMinutes } from '@/versions/v1/shared/clock.derive';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { FAILURE_MODES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { seriesAt, trendBundle } from '@/versions/v1/shared/series.derive';
import { bm01BearingStopComparison, bm01BearingTrend, bm01BearingTrendPhrase } from '@/versions/v1/modules/downtime/ValueLost.transform';
import { BM01_BEARING_PROPOSED_STOP } from '@/versions/v1/shared/bunker-hill-bearing-stop';
import { evaluateCapacity, calculateEbitda, recoveriesAtThroughput } from '@/versions/v1/shared/CapacityEngine';
import { CAPACITY_SEED } from '@/versions/v1/shared/capacity-seed';
import { defaultSmelterPriceState, BUNKER_HILL_SMELTER_SCHEDULE } from '@/versions/v1/shared/bunker-hill-smelter-terms';
import { penaltyCostPerElement } from '@/versions/v1/shared/SmelterSettlementEngine';
import { capacityPlantState } from '@/versions/v1/modules/capacity/Scene.transform';
import { feedDesignStph, feedStphAt } from '@/versions/v1/modules/overview/LandingSeries.transform';
import { formatCurrencyExact } from '@/versions/v1/shared/money';

const DT_1_BM01_ID = 'dt-1-bm01-bearing';
const DT_2_P80_ID = 'dt-2-p80-excursion';
const DT_3_ZN_RECOVERY_ID = 'dt-3-zn-recovery';
const DT_4_CUSO4_ID = 'dt-4-cuso4-dosing';
const DT_5_LIME_ID = 'dt-5-lime-silo';
const DT_6_PB_IN_ZN_ID = 'dt-6-pb-in-zn-con';
const ZN_REC_SERIES_ID = 'rs-zn-rec';
const ZN_REC_READING_ID = 'rd-kpi-zn-rec';
const ZN_RECOVERY_TREND_POINTS = 7;
const PB_IN_ZN_SERIES_ID = 'rs-spec-zn-pb';
const PB_IN_ZN_TREND_POINTS = 7;
const BM01_BEARING_SERIES_ID = 'rs-bm-brg-temp';
const BM01_BEARING_READING_ID = 'rd-bm-brg-temp';
const BM01_FAILURE_MODE_ID = 'fm-trunnion';
const BM01_TREND_POINTS = 6;
const DAILY_CADENCE_MINUTES = 24 * 60;

export type DriverPersonaProjection = 'operator' | 'metallurgist' | 'maintenance';

/** operator → priority chip, no dollars, numbered operator actions.
 *  metallurgist → dollars, diagnosis.
 *  maintenance → dollars, maintenance actions.
 *  Everyone else (Superintendent, Manager, …) reads the metallurgist
 *  projection read-only — they were not drawn a role of their own on this
 *  surface (docs/plans/glass-poc-run/decisions.md). */
export function projectionForPersona(persona: Persona): DriverPersonaProjection {
  switch (persona) {
    case 'Operator':
    case 'Control room':
      return 'operator';
    case 'Maintenance':
      return 'maintenance';
    default:
      return 'metallurgist';
  }
}

function znRecoveryDesign(): number {
  const reading = READINGS.find((row) => row.id === ZN_REC_READING_ID);
  if (!reading?.design) {
    throw new Error(`driver-tree: missing design for ${ZN_REC_READING_ID}`);
  }
  return reading.design;
}

function formatRecoveryPct(value: number): string {
  return value.toFixed(1);
}

function formatPptGap(gap: number): string {
  return gap.toFixed(1);
}

function formatTemp(value: number): string {
  return value.toFixed(1);
}

function formatMicron(value: number): string {
  return String(Math.round(value));
}

function formatPbPct(value: number): string {
  return value.toFixed(1);
}

/** Elapsed span across a trend: (n − 1) × cadence — intervals, not sample count. */
export function seriesElapsedMinutes(pointCount: number, cadenceMinutes: number): number {
  if (pointCount < 2) return 0;
  return (pointCount - 1) * cadenceMinutes;
}

function parseHourOffsetLabel(label: string): number | null {
  if (label === 'Now') return 0;
  const match = label.match(/^-(\d+)h$/);
  return match ? Number(match[1]) : null;
}

function cadenceMinutesFromCategories(categories: readonly string[]): number | null {
  if (categories.length < 2) return null;
  const first = parseHourOffsetLabel(categories[0]!);
  const second = parseHourOffsetLabel(categories[1]!);
  if (first !== null && second !== null) {
    return Math.abs(second - first) * 60;
  }
  if (/\d+\s+\w{3}/.test(categories[0]!) || /\w{3}\s+\d{1,2}/.test(categories[0]!)) {
    return DAILY_CADENCE_MINUTES;
  }
  return null;
}

function formatElapsedDuration(minutes: number): string {
  if (minutes % DAILY_CADENCE_MINUTES === 0 && minutes >= DAILY_CADENCE_MINUTES) {
    const days = minutes / DAILY_CADENCE_MINUTES;
    return days === 1 ? '1 day' : `${days} days`;
  }
  if (minutes % 60 === 0 && minutes >= 60) {
    const hours = minutes / 60;
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  return formatElapsedMinutes(minutes);
}

/** Compose "5 hours" / "6 days" from category labels and point count. */
export function seriesElapsedPhraseFromCategories(
  categories: readonly string[],
  pointCount: number,
): string {
  const cadence = cadenceMinutesFromCategories(categories);
  if (cadence === null) {
    throw new Error('driver-tree: cannot infer cadence from trend categories');
  }
  return formatElapsedDuration(seriesElapsedMinutes(pointCount, cadence));
}

/** Compose elapsed span from timed readings — cadence from the first interval. */
export function seriesElapsedPhraseFromTimedPoints(
  points: readonly { at: SeriesTime }[],
): string {
  if (points.length < 2) {
    throw new Error('driver-tree: fewer than two timed points for elapsed span');
  }
  const cadence = Math.abs(diffMinutes(points[1]!.at, points[0]!.at));
  return formatElapsedDuration(seriesElapsedMinutes(points.length, cadence));
}

/** BM01 current/warning/limit values, restart trend and stop horizon read the
 *  bearing series and its Reading rather than copy-level constants. */
export function groundBm01BearingIssue(issue: DriverIssueSeed, state: SessionState): DriverIssueSeed {
  if (issue.id !== DT_1_BM01_ID) return issue;
  const reading = READINGS.find((row) => row.id === BM01_BEARING_READING_ID);
  const series = seriesAt(BM01_BEARING_SERIES_ID, state);
  const points = series.points.filter((point) => point.value !== null).slice(-BM01_TREND_POINTS);
  if (!reading || points.length !== BM01_TREND_POINTS) {
    throw new Error('driver-tree: BM01 bearing trend requires six numeric points and rd-bm-brg-temp');
  }
  const y = points.map((point) => point.value as number);
  const x = points.map((_, index) => index === points.length - 1 ? 'Now' : `-${points.length - index - 1}h`);
  const alarm = reading.band?.hi;
  if (alarm === undefined) throw new Error('driver-tree: BM01 bearing missing its warning band');
  const stopComparison = bm01BearingStopComparison();
  const failureMode = FAILURE_MODES.find((row) => row.id === BM01_FAILURE_MODE_ID);
  if (!failureMode) throw new Error(`driver-tree: missing FMEA row ${BM01_FAILURE_MODE_ID}`);
  const current = y[y.length - 1]!;
  const previous = y[y.length - 2]!;
  const bearingRate = bm01BearingTrend(state);
  const limit = reading.spec?.hi;
  if (limit === undefined) throw new Error('driver-tree: BM01 bearing missing its 85 °C specification limit');
  const stopDate = `${BM01_BEARING_PROPOSED_STOP.label} ${BM01_BEARING_PROPOSED_STOP.shift} shift`;
  const trendPhrase = bm01BearingTrendPhrase(bearingRate);
  const what =
    `BM01 trunnion bearing is at ${formatTemp(current)}°C against the ${formatTemp(alarm)}°C warning; ${trendPhrase}. Elevated bearing temperature indicates increasing friction, which typically signals early bearing wear or lubrication degradation. A controlled stop is proposed for ${stopDate}, before the projected ${formatTemp(limit)}°C limit. Uncontrolled bearing failure causes a full mill seizure — the single largest unplanned event on site.`;

  const operatorActions = issue.operatorActions.map((action, index) => {
    if (index === 0) {
      return `Log TE-622-625 temperature every hour in shift sheet. Current: ${formatTemp(current)}°C. Previous hour: ${formatTemp(previous)}°C.`;
    }
    if (index === 1) {
      const sentenceTrend = `${trendPhrase[0]!.toUpperCase()}${trendPhrase.slice(1)}`;
      return `${sentenceTrend}. Call Maintenance Supervisor to keep the proposed ${stopDate} stop ahead of the projected limit.`;
    }
    return action;
  });

  const metDiagnosis = issue.metDiagnosis
    .replace(/RPN\s+\d+/, `RPN ${failureMode.rpn}`)
    .replace(/Financial:.*?\. Schedule inspection now\./,
      `Financial: planned inspection ${stopComparison.plannedHours} h ${formatExposurePerDay(stopComparison.plannedAmount)} vs avoided failure ${stopComparison.avoidedFailureHours} h ${formatExposurePerDay(stopComparison.avoidedFailureAmount)}. Schedule inspection now.`);
  const impactNote = `One-off bearing stop comparison · planned inspection ${stopComparison.plannedHours} h ${formatExposurePerDay(stopComparison.plannedAmount)} vs avoided failure ${stopComparison.avoidedFailureHours} h ${formatExposurePerDay(stopComparison.avoidedFailureAmount)}`;
  return {
    ...issue,
    stopComparison,
    trend: { ...issue.trend, x, y, alarm },
    what,
    metDiagnosis,
    impactNote,
    operatorActions,
  };
}

/** Select the highest counted issue in the projection layer so the UI stays
 *  presentational and the source guard can catch raw-impact sums. */
export function topCountedIssue(issues: readonly DriverIssue[]): DriverIssue {
  const counted = issues.filter((issue) => issue.countsTowardTotal);
  if (counted.length === 0) throw new Error('driver-tree: no counted issue to anchor exposure bars');
  const highest = Math.max(...counted.map((issue) => issue.impactPerDay));
  const top = counted.find((issue) => issue.impactPerDay === highest);
  if (!top) throw new Error('driver-tree: could not select the highest counted issue');
  return top;
}

/** P80 endpoints, target, elapsed span and last-three readings must match the
 *  sparkline series (D1). The feed figure reads the measured `rd-kpi-ore-td`
 *  over 24 h against its design (71.7 vs 75.0 stph at the demo cursor), never
 *  the retired 60→57 tph narrative (#733). */
export function groundP80Issue(issue: DriverIssueSeed, state: SessionState): DriverIssueSeed {
  if (issue.id !== DT_2_P80_ID) return issue;

  const { x, y, target } = issue.trend;
  const lastThree = y.slice(-3);
  if (lastThree.length < 3) return issue;
  if (target === undefined) {
    throw new Error('driver-tree: P80 trend missing target');
  }

  const first = y[0]!;
  const latest = y[y.length - 1]!;
  const readings = lastThree.map(formatMicron).join(', ');
  const targetLabel = formatMicron(target);
  const elapsed = seriesElapsedPhraseFromCategories(x, y.length);
  const feed = feedStphAt(state).toFixed(1);
  const feedDesign = feedDesignStph(state).toFixed(1);
  const what =
    `The cyclone overflow particle size (P80) has risen from ${formatMicron(first)} µm to ${formatMicron(latest)} µm over the past ${elapsed}, meaning ore is not being ground as fine as required. A coarser grind directly reduces both zinc and lead recovery — larger particles contain locked mineral grains that flotation cannot liberate. Throughput has been reduced to compensate, compounding the production loss.`;
  const why =
    `Ore hardness has increased in the current stope. Harder ore requires more grinding energy to reach the same target size — at the same feed rate, the mill cannot keep up, so P80 rises. Mill feed is ${feed} stph against its ${feedDesign} stph design, held there to bring P80 back toward the ${targetLabel} µm target. Mine planning can request softer ore from an adjacent stope if hardness persists beyond today.`;

  const operatorActions = issue.operatorActions.map((action, index) => {
    if (index === 0) {
      return `Feed rate held at ${feed} stph. Confirm DCS WIT-021 reads ${feed} ±1 stph. Hold if above ${(Number(feed) + 1).toFixed(1)} stph.`;
    }
    if (index === 2) {
      return `Log PSA-001 P80 every 30 minutes. Target: ≤${targetLabel} µm for 4 consecutive hours before any increase.`;
    }
    if (index === 3) {
      return `Current: ${formatMicron(latest)} µm. Last 3 readings: ${readings} µm — stable but still above USL.`;
    }
    return action;
  });

  const metDiagnosis =
    'Ore hardness increase (78% confidence) — leading cause. Competing: cyclone spigot wear 14% (CY-04 at 11% — not primary but do not exclude), ball charge 8%. Discriminating: measure CY-04 spigot — if >13mm ID reclassify to spigot wear. Fix P80 first — affects both Pb and Zn simultaneously.';

  return { ...issue, what, why, operatorActions, metDiagnosis };
}

/** CuSO4 target and current dose in operator actions must match the sparkline series (D1). */
export function groundCuso4Issue(issue: DriverIssueSeed): DriverIssueSeed {
  if (issue.id !== DT_4_CUSO4_ID) return issue;

  const { y, target } = issue.trend;
  if (target === undefined) {
    throw new Error('driver-tree: CuSO4 trend missing target');
  }
  const latest = y[y.length - 1]!;
  const operatorActions = issue.operatorActions.map((action, index) => {
    if (index === 0) {
      return `New batch prepared by the Met team and switched to the new tank at 10:00; FIT-CuSO4 logged within 5 min of the switch, target >${target} g/t.`;
    }
    if (index === 2) {
      return `Current FIT-CuSO4: ${latest} g/t. If drops below 460 g/t before batch is ready, call Met immediately — Tier 2 triggers.`;
    }
    return action;
  });

  return { ...issue, operatorActions };
}

/** Lime silo title, what/why, target/alarm and containment action must match the series (D1). */
export function groundLimeSiloIssue(issue: DriverIssueSeed): DriverIssueSeed {
  if (issue.id !== DT_5_LIME_ID) return issue;

  const { x, y, target, alarm } = issue.trend;
  if (target === undefined || alarm === undefined) {
    throw new Error('driver-tree: lime silo trend missing target or alarm');
  }

  const first = y[0]!;
  const latest = y[y.length - 1]!;
  const elapsed = seriesElapsedPhraseFromCategories(x, y.length);
  const targetLabel = String(target);
  const alarmLabel = String(alarm);
  const title = `Lime silo LT-500 at ${latest}% — below ${targetLabel}% LSL`;
  const what =
    `The lime silo level has dropped from ${first}% to ${latest}% over the past ${elapsed} and is now below the ${targetLabel}% low-level alarm. Lime (calcium hydroxide) controls the alkalinity (pH) in the zinc flotation circuit. Without sufficient lime, pH falls below the 11.0 operating target, reducing zinc mineral activation and directly suppressing zinc recovery. The silo must not fall below ${alarmLabel}% before the 21:00 delivery arrives.`;
  const why =
    `Lime consumption has depleted the silo faster than the replenishment schedule anticipated. A delivery has been confirmed for 21:00 tonight. If the silo drops below ${alarmLabel}% before delivery, manual lime addition from bagged product stored in the reagent shed will be required (approximately 2 hours of supply available). The lime silo depletion is the primary root cause of the current pH excursion and Zn recovery shortfall.`;

  const operatorActions = issue.operatorActions.map((action, index) => {
    if (index === 1) {
      return `If LT-500 drops below ${alarmLabel}% before delivery: switch both Pb and Zn circuits to manual lime addition. Rates: Pb 4.2 kg/t, Zn 2.8 kg/t. Bagged lime in reagent shed — 14 bags (~2 hours supply).`;
    }
    return action;
  });

  return { ...issue, title, what, why, operatorActions };
}

/** Paul's Zn recovery figures on issue #3 are illustrative; the series and
 *  design basis win (docs/plans/glass-poc-run/decisions.md § recovery basis). */
export function groundZnRecoveryIssue(issue: DriverIssueSeed, state: SessionState): DriverIssueSeed {
  if (issue.id !== DT_3_ZN_RECOVERY_ID) return issue;

  const design = znRecoveryDesign();
  const bundle = trendBundle([ZN_REC_SERIES_ID], state, { points: ZN_RECOVERY_TREND_POINTS });
  const points = bundle.series[0]?.points ?? [];
  if (points.length < 2) {
    throw new Error('driver-tree: rs-zn-rec has fewer than two points for the recovery trend');
  }

  const first = points[0]!;
  const latest = points[points.length - 1]!;
  const gapToDesign = design - latest;
  const periodLoss = first - latest;
  const categories = [...bundle.categories];
  if (categories.length > 0) categories[categories.length - 1] = 'Now';
  const anchorView = seriesAt(ZN_REC_SERIES_ID, state);
  const anchorNumeric = anchorView.points.filter(
    (p): p is { at: SeriesTime; value: number } => p.value !== null,
  );
  const elapsed = seriesElapsedPhraseFromTimedPoints(
    anchorNumeric.slice(-ZN_RECOVERY_TREND_POINTS),
  );

  const declineSentence =
    periodLoss > 0
      ? `Zinc recovery has declined from ${formatRecoveryPct(first)}% to ${formatRecoveryPct(latest)}% over ${elapsed} — a ${formatPptGap(periodLoss)} pt loss. `
      : `Zinc recovery is ${formatRecoveryPct(latest)}% over the last ${elapsed}. `;
  const definitionSentence = issue.what.trim();

  return {
    ...issue,
    title: `Zn recovery ${formatRecoveryPct(latest)}% — ${formatPptGap(gapToDesign)} pt below design (Tier 3)`,
    trend: {
      label: 'Zn recovery (%)',
      x: categories,
      y: [...points],
      target: design,
      badDirection: 'down',
    },
    what: `${declineSentence}${definitionSentence}`,
    operatorWhat: `${declineSentence}${issue.operatorWhat ?? definitionSentence}`,
  };
}

/** Issue #6 Pb-in-Zn reads rs-spec-zn-pb (2.6 % at the cursor), not Paul's 2.4 %
 *  (decisions.md § recovery basis). */
export function groundPbInZnIssue(issue: DriverIssueSeed, state: SessionState): DriverIssueSeed {
  if (issue.id !== DT_6_PB_IN_ZN_ID) return issue;

  const bundle = trendBundle([PB_IN_ZN_SERIES_ID], state, { points: PB_IN_ZN_TREND_POINTS });
  const points = bundle.series[0]?.points ?? [];
  if (points.length < 2) {
    throw new Error('driver-tree: rs-spec-zn-pb has fewer than two points for the 7-day trend');
  }

  const first = points[0]!;
  const latest = points[points.length - 1]!;
  const alarm = issue.trend.alarm ?? 3.0;
  const headroom = alarm - latest;
  const categories = [...bundle.categories];
  if (categories.length > 0) categories[categories.length - 1] = 'Now';

  const anchorView = seriesAt(PB_IN_ZN_SERIES_ID, state);
  const anchorNumeric = anchorView.points.filter(
    (p): p is { at: SeriesTime; value: number } => p.value !== null,
  );
  const slice = anchorNumeric.slice(-PB_IN_ZN_TREND_POINTS);
  const elapsed = seriesElapsedPhraseFromTimedPoints(slice);

  const ratioDeclinePrefix =
    'The ZnSO4/SMBS reagent ratio has declined from 0.86 to 0.82 over 8 consecutive readings. SMBS (sodium metabisulphite) is used to depress (suppress) lead in the zinc circuit — an insufficient dose allows lead minerals to float with the zinc and report to the zinc concentrate. The Metallurgist has proposed a 12% SMBS dose increase. ';
  const why =
    `${ratioDeclinePrefix}At ${formatPbPct(latest)}% there is ${formatPptGap(headroom)} pt of headroom before the ${formatPbPct(alarm)}% penalty triggers.`;
  const metDiagnosis =
    `Pb deportment (72% confidence). ZnSO4/SMBS dose ratio declined 0.86→0.82 over 8 readings — primary driver of reduced selectivity. Increase SMBS dose by 12%. At ${formatPbPct(latest)}% there is ${formatPptGap(headroom)} pt headroom before the ${formatPbPct(alarm)}% penalty trigger.`;

  return {
    ...issue,
    title: `Pb in Zn con ${formatPbPct(latest)}% — trending toward ${formatPbPct(alarm)}% smelter penalty`,
    trend: {
      ...issue.trend,
      x: categories,
      y: [...points],
    },
    what: `The lead content in the zinc concentrate has been rising steadily from ${formatPbPct(first)}% to ${formatPbPct(latest)}% over ${elapsed}. If it reaches ${formatPbPct(alarm)}%, the smelter will apply an impurity penalty deduction to the zinc concentrate payment. Lead in the zinc concentrate means the flotation circuit is not selectively rejecting lead when recovering zinc — both minerals are reporting to the zinc product.`,
    why,
    metDiagnosis,
  };
}

type DriverFinancialContext = {
  plantState: ReturnType<typeof capacityPlantState>;
  prices: ReturnType<typeof defaultSmelterPriceState>;
  capacity: ReturnType<typeof evaluateCapacity>;
};

function driverFinancialContext(state: SessionState): DriverFinancialContext {
  const plantState = capacityPlantState(state);
  const prices = defaultSmelterPriceState();
  const capacity = evaluateCapacity({ grade: plantState.grade, seed: CAPACITY_SEED, prices, plantState });
  return { plantState, prices, capacity };
}

function znRecoveryGapPpt(context: DriverFinancialContext): number {
  return Math.max(0, znRecoveryDesign() - context.plantState.recoveries.znPct);
}

/** A physical attribution multiplied by its live engine value per unit. */
function valueForBasis(basis: DriverIssueValueBasis, context: DriverFinancialContext): number {
  const { plantState, prices, capacity } = context;
  const currentFinancials = capacity.scenarios.find((row) => row.id === 'current')!.financials;
  switch (basis.kind) {
    case 'recovery':
      return Math.max(0, basis.ppt * (basis.metal === 'Zn' ? capacity.perPpt.zincA : capacity.perPpt.leadSilverA));
    case 'recovery-gap':
    case 'recovery-share': {
      const gapPpt = Math.round(znRecoveryGapPpt(context) * 100) / 100;
      const zincDollarsPerPpt = Math.round(capacity.perPpt.zincA * 100) / 100;
      const share = basis.kind === 'recovery-gap' ? 1 : basis.fraction;
      return Math.round(gapPpt * share * zincDollarsPerPpt * 100) / 100;
    }
    case 'throughput': {
      const reducedFeed = Math.max(0, plantState.feedStph - basis.lostStph);
      const recoveries = recoveriesAtThroughput(reducedFeed, CAPACITY_SEED, false, plantState.grade, plantState.recoveries, plantState);
      const reduced = calculateEbitda(reducedFeed, plantState.grade, recoveries, CAPACITY_SEED, prices, plantState);
      return Math.max(0, currentFinancials.ebitdaA - reduced.ebitdaA);
    }
    case 'schedule-penalty': {
      const assay = plantState.penaltyAssays?.zincPbPct;
      if (assay === undefined) throw new Error('driver-tree: missing current Pb-in-Zn penalty assay');
      return penaltyCostPerElement(
        basis.product,
        basis.element,
        assay,
        currentFinancials.zinc.concentrateDmtPerDay,
      );
    }
  }
}

function pricedIssue(issue: DriverIssueSeed, context: DriverFinancialContext): DriverIssue {
  const countedAmountPerDay = issue.countedBasis ? valueForBasis(issue.countedBasis, context) : undefined;
  const sharedPathContribution: (NonNullable<DriverIssue['sharedPathContribution']>) | undefined = issue.sharedPathContribution
    ? { ...issue.sharedPathContribution, impactPerDay: valueForBasis(issue.sharedPathContribution.basis, context) }
    : undefined;
  if (issue.countsTowardTotal && countedAmountPerDay === undefined) {
    throw new Error(`driver-tree seed: counted issue "${issue.id}" has no physical value basis`);
  }
  if (!issue.countsTowardTotal && !sharedPathContribution && !issue.stopComparison) {
    throw new Error(`driver-tree seed: non-counting issue "${issue.id}" has no shared-path basis`);
  }
  const impactPerDay = issue.countsTowardTotal
    ? countedAmountPerDay! + (sharedPathContribution?.impactPerDay ?? 0)
    : sharedPathContribution?.impactPerDay ?? 0;
  const presentation = { ...issue };
  delete presentation.countedBasis;
  delete presentation.impactPerDay;
  delete presentation.countedAmountPerDay;
  delete presentation.sharedPathContribution;
  const result = {
    ...presentation,
    impactPerDay,
    ...(countedAmountPerDay !== undefined ? { countedAmountPerDay } : {}),
    ...(sharedPathContribution ? { sharedPathContribution } : {}),
  } as DriverIssue;
  const penaltyBasis = issue.countedBasis;
  if (issue.id === DT_6_PB_IN_ZN_ID && penaltyBasis?.kind === 'schedule-penalty' && countedAmountPerDay !== undefined) {
    const productSchedule = BUNKER_HILL_SMELTER_SCHEDULE.products.find((row) => row.product === penaltyBasis.product)!;
    const penalty = productSchedule.penalties.find((term) => term.element === penaltyBasis.element)!;
    const zincConcentrateDmt = context.capacity.scenarios.find((row) => row.id === 'current')!.financials.zinc.concentrateDmtPerDay;
    const assay = context.plantState.penaltyAssays?.zincPbPct;
    if (assay === undefined) throw new Error('driver-tree: missing current Pb-in-Zn penalty assay');
    const valuePerTenthPct = penaltyCostPerElement(
      penaltyBasis.product,
      penaltyBasis.element,
      penalty.limit + 0.1,
      zincConcentrateDmt,
    );
    result.impactNote = `Watch · penalty ${formatCurrencyExact(countedAmountPerDay)}/day · ${formatCurrencyExact(valuePerTenthPct)}/day per 0.1% above ${penalty.limit.toFixed(1)}%`;
    result.operatorImpactNote = `Watch · ${assay.toFixed(1)} % Pb in Zn concentrate vs ${penalty.limit.toFixed(1)} % penalty limit`;
  }
  return result;
}

/** Whole-cent parts of `totalCents` across `fractions` by largest remainder:
 *  each part is a whole cent and the parts sum to the total exactly. */
function allocateCents(totalCents: number, fractions: readonly number[]): number[] {
  const exact = fractions.map((fraction) => totalCents * fraction);
  const cents = exact.map((amount) => Math.floor(amount));
  let remainder = totalCents - cents.reduce((sum, amount) => sum + amount, 0);
  const order = exact
    .map((amount, index) => ({ index, part: amount - Math.floor(amount) }))
    .sort((a, b) => b.part - a.part || a.index - b.index);
  for (const { index } of order) {
    if (remainder <= 0) break;
    cents[index] += 1;
    remainder -= 1;
  }
  return cents;
}

/**
 * A compound owner's counted amount is one priced gap and its contributors'
 * shares are fractions of that same gap. Each share is rounded to cents on its
 * own, so the set can sum a cent over its owner; the exposure partition then
 * reads an owner open amount smaller than its live shares and refuses (#785 —
 * 07 Jun 11:30 and 05 Jun 11:30, two of the walkthrough's step-back cursors).
 * Round the owner's cents across its shares by largest remainder instead: the
 * shares partition the owner exactly at every cursor, the owner keeps its
 * grounded value, and no dollar is counted twice. A share set whose fractions
 * leave a genuine residual is not reconciled — that residual stays with the
 * owner's own impact type, as the partition's rule requires.
 */
function reconcileCompoundShares(issues: DriverIssue[]): DriverIssue[] {
  const contributorsByOwner = new Map<string, DriverIssue[]>();
  for (const issue of issues) {
    const ownerId = issue.sharedPathContribution?.ownerIssueId;
    if (!ownerId) continue;
    const existing = contributorsByOwner.get(ownerId);
    if (existing) existing.push(issue);
    else contributorsByOwner.set(ownerId, [issue]);
  }
  if (contributorsByOwner.size === 0) return issues;
  const allocated = new Map<string, number>();
  for (const [ownerId, contributors] of contributorsByOwner) {
    const owner = issues.find((issue) => issue.id === ownerId);
    if (owner?.countedAmountPerDay === undefined) continue;
    const fractions: number[] = [];
    for (const contributor of contributors) {
      const basis = contributor.sharedPathContribution!.basis;
      if (basis.kind !== 'recovery-share') break;
      fractions.push(basis.fraction);
    }
    if (fractions.length !== contributors.length) continue;
    if (Math.abs(fractions.reduce((sum, fraction) => sum + fraction, 0) - 1) > 1e-9) continue;
    const cents = allocateCents(Math.round(owner.countedAmountPerDay * 100), fractions);
    contributors.forEach((contributor, index) => allocated.set(contributor.id, cents[index]! / 100));
  }
  if (allocated.size === 0) return issues;
  return issues.map((issue) => {
    const shareAmount = allocated.get(issue.id);
    if (shareAmount === undefined) return issue;
    return {
      ...issue,
      sharedPathContribution: { ...issue.sharedPathContribution!, impactPerDay: shareAmount },
      impactPerDay: issue.countsTowardTotal && issue.countedAmountPerDay !== undefined
        ? issue.countedAmountPerDay + shareAmount
        : shareAmount,
    };
  });
}

/** The P80 row's diagnosis repeats its two priced arms, so it is rebuilt once
 *  the shares are reconciled to their owner's cents (#785): the diagnosis and the
 *  ledger bar always print the same share. */
function p80ShareDiagnosis(issue: DriverIssue, context: DriverFinancialContext): DriverIssue {
  if (issue.id !== DT_2_P80_ID) return issue;
  const countedAmountPerDay = issue.countedAmountPerDay;
  const shared = issue.sharedPathContribution;
  if (countedAmountPerDay === undefined || !shared) return issue;
  const shareAmount = shared.impactPerDay ?? 0;
  const shareDescription = shared.basis.kind === 'recovery-share'
    ? `${(znRecoveryGapPpt(context) * shared.basis.fraction).toFixed(2)} pt Zn recovery`
    : 'recovery';
  const fullAmount = countedAmountPerDay + shareAmount;
  return {
    ...issue,
    metDiagnosis: `${issue.metDiagnosis} P80 attributes ${formatCurrencyExact(countedAmountPerDay)}/day to a 3 stph feed loss and ${formatCurrencyExact(shareAmount)}/day to ${shareDescription} (${formatCurrencyExact(fullAmount)}/day total).`,
  };
}

/** Ground series-backed issues, project their physical values, then rank. */
export function prepareIssues(seed: DriverTreeSeed, state: SessionState): DriverIssue[] {
  const grounded = seed.issues.map((issue) => {
    let next = groundZnRecoveryIssue(issue, state);
    next = groundBm01BearingIssue(next, state);
    next = groundP80Issue(next, state);
    next = groundCuso4Issue(next);
    next = groundLimeSiloIssue(next);
    next = groundPbInZnIssue(next, state);
    return next;
  });
  const context = driverFinancialContext(state);
  const priced = reconcileCompoundShares(grounded.map((issue) => pricedIssue(issue, context)));
  return rankIssues({ ...seed, issues: priced.map((issue) => p80ShareDiagnosis(issue, context)) });
}

export function pricedDriverTree(seed: DriverTreeSeed, state: SessionState): PricedDriverTree {
  return { ...seed, issues: prepareIssues(seed, state) };
}

/** Preserve Paul's flat order and validate the declared ranks. Full engine row
 *  amounts now drive the proportions, so sorting by amount would move Zn
 *  recovery ahead of P80 and break the drawn structure. */
export function rankIssues(seed: PricedDriverTree): DriverIssue[] {
  const ordered = [...seed.issues].sort((a, b) => a.rank - b.rank);
  return ordered.map((issue, index) => {
    const rank = index + 1;
    if (issue.rank !== rank) {
      throw new Error(
        `driver-tree seed: "${issue.title}" row rank ${rank} does not match seed rank ${issue.rank}`,
      );
    }
    return { ...issue, rank };
  });
}

export interface ExposureBreakdownPerDay {
  active: number;
  conditional: number;
  captured: number;
  combined: number;
}

export interface IssueExposureView {
  /** Amount shown as the row's primary figure; shared-path-only rows show their share. */
  amountPerDay: number;
  /** Full engine row amount used for the proportion bar. */
  barAmountPerDay: number;
  contributor: boolean;
  /** The owner's rank for a non-counting shared-path share. */
  ownerRank?: number;
  /** A counted row can also carry a non-counting share into its owner. */
  recoverySharePerDay?: number;
  stopComparison?: DriverIssue['stopComparison'];
  /** Shares attributed to this issue, rendered as its embedded breakdown. */
  breakdown: { issueId: string; label: string; amountPerDay: number }[];
}

/** Operator cards omit monetary owner-breakdown rows along with the dollar projection. */
export function projectedBreakdown(
  exposure: IssueExposureView,
  projection: DriverPersonaProjection,
): IssueExposureView['breakdown'] {
  return projection === 'operator' ? [] : exposure.breakdown;
}

/** The driver-tree seed's `resolvedAt` is the shared issue-resolution clock. */
export function driverIssueResolvedAtCursor(issue: DriverIssue, cursor?: SeriesTime): boolean {
  return Boolean(cursor && issue.resolvedAt && issue.resolvedAt <= cursor);
}

/** One resolved child share leaves its counted owner's open amount. */
export function openCountedAmountPerDay(
  issue: DriverIssue,
  seed: PricedDriverTree,
  cursor?: SeriesTime,
): number {
  const counted = issue.countedAmountPerDay ?? issue.impactPerDay;
  if (!cursor) return counted;
  let capturedShares = 0;
  for (const candidate of seed.issues) {
    const contribution = candidate.sharedPathContribution;
    if (contribution?.ownerIssueId === issue.id && driverIssueResolvedAtCursor(candidate, cursor)) {
      capturedShares += contribution.impactPerDay;
    }
  }
  return Math.max(0, Math.round((counted - capturedShares) * 100) / 100);
}

export function driverIssueResolutionLabel(issue: DriverIssue, cursor?: SeriesTime): string | null {
  if (!issue.resolvedAt || !driverIssueResolvedAtCursor(issue, cursor)) return null;
  return `Resolved · ${formatDayMonth(issue.resolvedAt as SeriesTime)} ${issue.resolvedAt.slice(11, 16)}`;
}

export function activeDriverIssueCount(issues: readonly DriverIssue[], cursor?: SeriesTime): number {
  return issues.filter((issue) => !driverIssueResolvedAtCursor(issue, cursor)).length;
}

/** Resolve one row's counted display amount, bar amount and owner breakdown from the seed. */
export function issueExposureView(
  issue: DriverIssue,
  seed: PricedDriverTree,
  cursor?: SeriesTime,
): IssueExposureView {
  const shared = issue.sharedPathContribution;
  const owner = shared
    ? seed.issues.find((candidate) => candidate.id === shared.ownerIssueId)
    : undefined;
  const amountPerDay = issue.countsTowardTotal
    ? openCountedAmountPerDay(issue, seed, cursor)
    : issue.stopComparison ? 0 : shared?.impactPerDay;
  if (typeof amountPerDay !== 'number' || !Number.isFinite(amountPerDay)) {
    throw new Error(`driver-tree seed: issue "${issue.id}" has no finite display exposure amount`);
  }

  const breakdown = seed.issues.flatMap((candidate) => {
    const contribution = candidate.sharedPathContribution;
    if (contribution?.ownerIssueId !== issue.id || driverIssueResolvedAtCursor(candidate, cursor)) return [];
    return [{
      issueId: candidate.id,
      label: candidate.id === DT_2_P80_ID ? 'P80 recovery share' : candidate.shortLabel,
      amountPerDay: contribution.impactPerDay,
    }];
  });

  return {
    amountPerDay,
    barAmountPerDay: issue.countsTowardTotal
      ? openCountedAmountPerDay(issue, seed, cursor) + (shared?.impactPerDay ?? 0)
      : issue.stopComparison ? 0 : issue.impactPerDay,
    contributor: issue.attribution === 'contributor',
    ...(issue.stopComparison ? { stopComparison: issue.stopComparison } : {}),
    ...(owner ? { ownerRank: owner.rank } : {}),
    ...(issue.countsTowardTotal && shared ? { recoverySharePerDay: shared.impactPerDay } : {}),
    breakdown,
  };
}

function sumCountedExposurePerDay(
  seed: PricedDriverTree,
  period?: 'active' | 'conditional',
  cursor?: SeriesTime,
): number {
  return seed.issues.reduce((sum, issue) => {
    if (issue.attribution === 'contributor' && issue.countsTowardTotal) {
      throw new Error(`driver-tree seed: contributor "${issue.id}" cannot count toward exposure`);
    }
    if (issue.attribution !== 'contributor' && !issue.countsTowardTotal && !issue.stopComparison) {
      throw new Error(`driver-tree seed: ${issue.attribution} "${issue.id}" must count toward exposure`);
    }
    if (!issue.countsTowardTotal || (period && issue.exposurePeriod !== period)) return sum;
    if (typeof issue.countedAmountPerDay !== 'number' || !Number.isFinite(issue.countedAmountPerDay)) {
      throw new Error(`driver-tree seed: counted issue "${issue.id}" is missing a finite countedAmountPerDay`);
    }
    return sum + openCountedAmountPerDay(issue, seed, cursor);
  }, 0);
}

/** Sums only the explicitly counted owner and independent amounts. */
export function totalExposurePerDay(seed: PricedDriverTree): number {
  return sumCountedExposurePerDay(seed);
}

/** Separates active exposure from conditional exposure and retains their combined headline. */
export function exposureBreakdownPerDay(seed: PricedDriverTree, cursor?: SeriesTime): ExposureBreakdownPerDay {
  let captured = 0;
  for (const issue of seed.issues) {
    if (!issue.sharedPathContribution || !driverIssueResolvedAtCursor(issue, cursor)) continue;
    captured += issue.sharedPathContribution.impactPerDay;
  }
  return {
    active: sumCountedExposurePerDay(seed, 'active', cursor),
    conditional: sumCountedExposurePerDay(seed, 'conditional', cursor),
    captured,
    combined: totalExposurePerDay(seed),
  };
}

/** The D20 headline: one figure over the open cases at the cursor, split by the
 *  spec's three impact types. `total` is open counted exposure only — captured
 *  value leaves the headline and belongs to the Value stage.
 *
 *  Every open counted dollar is attributed to the impact type of the issue whose
 *  physical basis prices it. A compound owner's open amount is split across its
 *  live contributor shares (at the demo cursor P80's recovery arm is Active loss
 *  and the lime silo's is Risk); any residual stays with the owner's own type.
 *  A resolved contributor share is captured, so it leaves both the total and the
 *  split. The split sums to the total to the cent by construction. */
export interface OpenCaseExposure {
  total: number;
  activeLoss: number;
  risk: number;
  accruingPenalty: number;
}

const IMPACT_TYPE_FIELD: Readonly<Record<DriverIssueImpactType, keyof Omit<OpenCaseExposure, 'total'>>> = {
  'Active loss': 'activeLoss',
  Risk: 'risk',
  'Accruing penalty': 'accruingPenalty',
};

function roundCents(amount: number): number {
  return Math.round(amount * 100) / 100;
}

/**
 * `isOpenCase` lets a caller that owns the case-stage clock (the flow landing)
 * exclude an issue whose linked case has not been raised yet — and, with it, any
 * counted issue that links to no case. The driver tree and the role landings read
 * the seed's own `resolvedAt` clock alone; the two agree at the demo cursor.
 */
export function openCaseExposurePerDay(
  seed: PricedDriverTree,
  cursor?: SeriesTime,
  isOpenCase?: (caseId: string) => boolean,
): OpenCaseExposure {
  const exposure: OpenCaseExposure = { total: 0, activeLoss: 0, risk: 0, accruingPenalty: 0 };
  for (const issue of seed.issues) {
    if (!issue.countsTowardTotal) continue;
    if (driverIssueResolvedAtCursor(issue, cursor)) continue;
    if (isOpenCase && (!issue.caseId || !isOpenCase(issue.caseId))) continue;
    const openAmount = openCountedAmountPerDay(issue, seed, cursor);
    if (typeof openAmount !== 'number' || !Number.isFinite(openAmount)) {
      throw new Error(`exposure: counted issue "${issue.id}" has no finite open amount`);
    }
    let attributed = 0;
    for (const candidate of seed.issues) {
      const contribution = candidate.sharedPathContribution;
      if (contribution?.ownerIssueId !== issue.id) continue;
      if (driverIssueResolvedAtCursor(candidate, cursor)) continue;
      const shareAmount = contribution.impactPerDay;
      if (typeof shareAmount !== 'number' || !Number.isFinite(shareAmount)) {
        throw new Error(`exposure: contributor "${candidate.id}" has no finite priced share`);
      }
      exposure[IMPACT_TYPE_FIELD[candidate.impactType]] += shareAmount;
      attributed += shareAmount;
    }
    const residual = roundCents(openAmount - attributed);
    if (residual < 0) {
      throw new Error(`exposure: issue "${issue.id}" open amount is smaller than its live contributor shares`);
    }
    exposure[IMPACT_TYPE_FIELD[issue.impactType]] += residual;
    exposure.total += openAmount;
  }
  return {
    total: roundCents(exposure.total),
    activeLoss: roundCents(exposure.activeLoss),
    risk: roundCents(exposure.risk),
    accruingPenalty: roundCents(exposure.accruingPenalty),
  };
}

export interface ExposureHeadline {
  /** The headline figure, formatted once. */
  total: string;
  /** `<amount> active loss · <amount> risk · <amount> accruing penalty`. */
  split: string;
}

/** One spelling of the open-case headline for the tree, the flow landing and the role tiles. */
export function exposureHeadline(exposure: OpenCaseExposure): ExposureHeadline {
  const parts: Array<[string, number]> = [
    ['active loss', exposure.activeLoss],
    ['risk', exposure.risk],
    ['accruing penalty', exposure.accruingPenalty],
  ];
  return {
    total: formatExposurePerDay(exposure.total),
    split: parts.map(([label, amount]) => `${formatExposurePerDay(amount)} ${label}`).join(' · '),
  };
}

/** Formats the exposure value consistently across the tree and role landings. */
export function formatExposurePerDay(amount: number): string {
  return formatCurrencyExact(amount);
}

/** issue.impactPerDay / top.impactPerDay — the rank-1 issue's bar reads 100%. */
export function proportion(
  issue: DriverIssue,
  top: DriverIssue,
  seed?: PricedDriverTree,
  cursor?: SeriesTime,
): number {
  const issueAmount = seed ? issueExposureView(issue, seed, cursor).barAmountPerDay : issue.impactPerDay;
  const topAmount = seed ? issueExposureView(top, seed, cursor).barAmountPerDay : top.impactPerDay;
  if (topAmount <= 0) return 0;
  return issueAmount / topAmount;
}

export type BreadcrumbNode = { id: string; label: string; leaf: boolean };

/** Labels along `path`, leaf (the instrument tag) flagged. */
export function breadcrumb(issue: DriverIssue, nodes: DriverNode[]): BreadcrumbNode[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  return issue.path.map((id, index) => ({
    id,
    label: byId.get(id)?.label ?? id,
    leaf: index === issue.path.length - 1,
  }));
}

/** Resolves `issue.impactNote` for rendering: swaps in the dollar-free
 *  `operatorImpactNote` for the Operator projection (MUST-FIX 1), and
 *  prefixes a `contributesTo` cross-reference by the target issue's
 *  `shortLabel` — never "Contributing to Issue #N" (MUST-FIX 2). */
export function renderedImpactNote(
  issue: DriverIssue,
  seed: PricedDriverTree,
  projection: DriverPersonaProjection,
): string {
  const base = projection === 'operator' ? (issue.operatorImpactNote ?? issue.impactNote) : issue.impactNote;
  if (!issue.contributesTo) return base;
  const target = seed.issues.find((candidate) => candidate.id === issue.contributesTo);
  if (!target) return base;
  return `Contributing to ${target.shortLabel} · ${base}`;
}

/** Resolves `issue.what` for rendering: swaps in the dollar-free
 *  `operatorWhat` for the Operator projection (MUST-FIX 1). */
export function renderedWhat(issue: DriverIssue, projection: DriverPersonaProjection): string {
  return projection === 'operator' ? (issue.operatorWhat ?? issue.what) : issue.what;
}

/** The resolved note's first clause before '·' — the card's sub-caption.
 *  Takes the already-resolved (persona + contributesTo) note, not the raw
 *  seed field, so the rail and the body never show two different
 *  "Contributing to …" spellings. */
export function subCaption(note: string): string {
  return note.split('·')[0].trim();
}

/** The resolved note's remaining clauses after the first '·' — the body's
 *  secondary line. Empty when the note has no second clause, and the card
 *  renders nothing there (absence is never content) rather than repeating
 *  the sub-caption a second time (fixer round 1 MUST-FIX 5). */
export function secondaryNote(note: string): string {
  const clauses = note.split('·');
  return clauses.length > 1 ? clauses.slice(1).join('·').trim() : '';
}

/** Operator projection: count of issues by priority (severity), in
 *  descending severity order — replaces the dollar exposure tile
 *  (MUST-FIX 1). */
export function operatorPrioritySummary(
  issues: DriverIssue[],
  cursor?: SeriesTime,
): { severity: DriverIssue['severity']; label: string; count: number }[] {
  const order: DriverIssue['severity'][] = ['critical', 'high', 'medium'];
  const activeIssues = issues.filter((issue) => !driverIssueResolvedAtCursor(issue, cursor));
  return order
    .map((severity) => ({
      severity,
      label: priorityLabel(severity),
      count: activeIssues.filter((issue) => issue.severity === severity).length,
    }))
    .filter((row) => row.count > 0);
}

/** Throws on a cycle — the driver tree is a DAG (a tree, in this seed). */
export function validateDag(nodes: DriverNode[], edges: DriverEdge[]): void {
  const children = new Map<string, string[]>();
  for (const edge of edges) {
    if (!children.has(edge.to)) children.set(edge.to, []);
    children.get(edge.to)!.push(edge.from);
  }
  const known = new Set(nodes.map((node) => node.id));
  const visiting = new Set<string>();
  const visited = new Set<string>();

  function visit(id: string, trail: string[]): void {
    if (visited.has(id)) return;
    if (visiting.has(id)) {
      throw new Error(`driver-tree seed: cycle detected at "${id}" (${[...trail, id].join(' → ')})`);
    }
    if (!known.has(id)) return;
    visiting.add(id);
    for (const child of children.get(id) ?? []) {
      visit(child, [...trail, id]);
    }
    visiting.delete(id);
    visited.add(id);
  }

  for (const node of nodes) visit(node.id, []);
}

/** Padded y-domain for collapsed rail sparklines — keeps narrow recovery
 *  series readable instead of a near-flat hairline. */
export function sparklineYDomain(
  points: readonly number[],
  references: readonly number[] = [],
): [number, number] {
  const values = [...points, ...references];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pad = span * 0.15;
  return [min - pad, max + pad];
}

/** Trend view model feeding `TrendChart`: series + target/alarm reference
 *  lines + a unit-aware last-value label (the chart's own last-point label
 *  always appends '%', which is wrong for °C / g/t / µm / tph series — the
 *  scene renders the label text itself instead of using that prop). */
export function trendViewModel(issue: DriverIssue): {
  series: TrendSeries[];
  categories: string[];
  references: TrendReference[];
  lastValueLabel: string;
  yDomain?: readonly [number, number];
} {
  const { trend } = issue;
  // Every series here is an active excursion, so the line itself always
  // reads as the alarm tone regardless of which direction is "bad".
  const tone = 'bad' as const;
  const references: TrendReference[] = [];
  if (trend.target !== undefined) {
    references.push({ value: trend.target, label: 'target', tone: 'ok' });
  }
  if (trend.alarm !== undefined) {
    references.push({ value: trend.alarm, label: 'warning', tone: 'bad' });
  }
  const unit = trend.label.match(/\(([^)]+)\)\s*$/)?.[1] ?? '';
  const lastValue = trend.y[trend.y.length - 1];
  const refValues = references.map((ref) => ref.value);
  return {
    series: [{ name: trend.label, points: trend.y, tone, area: true }],
    categories: trend.x,
    references,
    lastValueLabel: unit ? `Now ${lastValue}${unit}` : `Now ${lastValue}`,
    yDomain: trend.y.length > 0 ? sparklineYDomain(trend.y, refValues) : undefined,
  };
}

/** TARP tier chip: one tone map for the Advisory module (polish.P28c, comp
 *  binding list). Tier 3 is the breach tier (`--state-blocked` on its wash),
 *  Tier 2 the warning tier, Tier 1 a neutral outline and Watch a neutral fill —
 *  never blue, never solid, never crimson. */
export type TarpChipTone = 'bad' | 'warn' | 'outline' | 'neutral';

export function tarpChip(issue: DriverIssue): { label: string; tone: TarpChipTone } {
  switch (issue.tarpTier) {
    case 1:
      return { label: 'Tier 1', tone: 'outline' };
    case 2:
      return { label: 'Tier 2', tone: 'warn' };
    case 3:
      return { label: 'Tier 3', tone: 'bad' };
    case 'watch':
      return { label: 'Watch', tone: 'neutral' };
  }
}

/** Hypothesis chip text — code + confidence when the rule id is not in the
 *  closed MD-0nn / FMR-nnnn family (empty `code`), label + confidence otherwise. */
export function hypothesisChipLabel(hypothesis: { code: string; label: string; confidence: number }): string {
  return hypothesis.code
    ? `${hypothesis.code} ${hypothesis.confidence}%`
    : `${hypothesis.label} ${hypothesis.confidence}%`;
}

export function priorityLabel(severity: DriverIssue['severity']): string {
  switch (severity) {
    case 'critical':
      return 'CRITICAL';
    case 'high':
      return 'HIGH';
    case 'medium':
      return 'MEDIUM';
  }
}

export function priorityTone(severity: DriverIssue['severity']): 'bad' | 'warn' | 'info' {
  switch (severity) {
    case 'critical':
      return 'bad';
    case 'high':
      return 'warn';
    case 'medium':
      return 'info';
  }
}

/** The issue order follows the seeded cause path, not the projected dollar amounts. */
export function activeIssuesMetaLine(_projection: DriverPersonaProjection, count: number): string {
  return `${count} active issues`;
}

// ---------------------------------------------------------------------------
// Value stage (polish.P28c, #715): the partition in the head of the
// driver-tree panel, the ledger rows under it and the selected issue's trend.
// Every figure here is read from the priced seed at the cursor; nothing is
// typed and nothing is summed twice.
// ---------------------------------------------------------------------------

export type ValuePartitionKind = 'active-loss' | 'risk' | 'accruing-penalty' | 'captured';

export type ValuePartitionNode = {
  issueId: string;
  label: string;
  amountPerDay: number;
  kind: ValuePartitionKind;
  /** Share of the partition's full width (0–100); the counted rows fill 88 %.
   *  Null: the node sizes to its label (a captured share is not an open width). */
  widthPct: number | null;
};

export type ValuePartition = {
  /** The open-case exposure the counted row sums to (D20). */
  totalPerDay: number;
  /** The counted issues at the cursor: the owner, the independent arm, the penalty. */
  counted: ValuePartitionNode[];
  /** The owner's open contributor shares, nested under it and not added again. */
  shares: ValuePartitionNode[];
  /** The owner whose width the shares fill. */
  ownerIssueId: string | null;
  /** Contributor shares resolved by the cursor: captured, out of the exposure. */
  captured: (ValuePartitionNode & { at: string; ownerLabel: string })[];
};

/** The counted row fills 88 % of the partition; a zero-valued node keeps 12 %. */
const PARTITION_SPAN_PCT = 88;
const PARTITION_ZERO_PCT = 12;

const IMPACT_KIND: Readonly<Record<DriverIssueImpactType, ValuePartitionKind>> = {
  'Active loss': 'active-loss',
  Risk: 'risk',
  'Accruing penalty': 'accruing-penalty',
};

function nodeLabel(nodes: readonly DriverNode[], id: string | undefined): string {
  return nodes.find((node) => node.id === id)?.label ?? id ?? '';
}

function countedNodeLabel(issue: DriverIssue, nodes: readonly DriverNode[], isOwner: boolean): string {
  if (isOwner) return `${nodeLabel(nodes, issue.path[1])} › ${nodeLabel(nodes, issue.path[2])} · owner`;
  if (issue.sharedPathContribution) {
    // The counted arm of an issue whose recovery share sits in another owner.
    return `Throughput › ${shareLabel(issue).replace(/ recovery share$/, '')} arm`;
  }
  return nodeLabel(nodes, issue.path[2]);
}

function shareLabel(issue: DriverIssue): string {
  return issue.id === DT_2_P80_ID ? 'P80 recovery share' : issue.shortLabel;
}

/**
 * C's partition, drawn in A's driver-tree head: EBITDA → the counted issues at
 * the cursor (their widths sum to the open-case exposure), the owner's open
 * contributor shares nested inside the owner's width (the no-double-count
 * rule), and the resolved shares drawn captured beneath. The one-off bearing
 * stop is not a daily amount and sits outside the partition (its ledger row).
 */
export function valuePartition(seed: PricedDriverTree, cursor: SeriesTime): ValuePartition {
  const exposure = openCaseExposurePerDay(seed, cursor);
  const total = exposure.total;
  const width = (amount: number) => (total > 0 ? Math.round((amount / total) * PARTITION_SPAN_PCT * 100) / 100 : 0);
  const openCounted = seed.issues.filter((issue) => issue.countsTowardTotal && !driverIssueResolvedAtCursor(issue, cursor));
  const shareOwnerIds = new Set(seed.issues.flatMap((issue) => issue.sharedPathContribution ? [issue.sharedPathContribution.ownerIssueId] : []));
  // The owner (it carries shares) leads, then the other counted rows by amount.
  const ordered = [...openCounted].sort((a, b) =>
    Number(shareOwnerIds.has(b.id)) - Number(shareOwnerIds.has(a.id)) ||
    openCountedAmountPerDay(b, seed, cursor) - openCountedAmountPerDay(a, seed, cursor));
  const counted = ordered.map((issue) => {
    const amountPerDay = openCountedAmountPerDay(issue, seed, cursor);
    const isOwner = shareOwnerIds.has(issue.id);
    return {
      issueId: issue.id,
      label: countedNodeLabel(issue, seed.nodes, isOwner),
      amountPerDay,
      kind: isOwner ? 'active-loss' as const : IMPACT_KIND[issue.impactType],
      widthPct: amountPerDay > 0 ? width(amountPerDay) : PARTITION_ZERO_PCT,
    };
  });
  const owner = ordered.find((issue) => shareOwnerIds.has(issue.id)) ?? null;
  const shares = owner
    ? seed.issues.flatMap((issue) => {
      const share = issue.sharedPathContribution;
      if (share?.ownerIssueId !== owner.id || driverIssueResolvedAtCursor(issue, cursor)) return [];
      const amountPerDay = share.impactPerDay ?? 0;
      return [{
        issueId: issue.id,
        label: `${shareLabel(issue)} · ${issue.impactType.toLowerCase()}`,
        amountPerDay,
        kind: IMPACT_KIND[issue.impactType],
        widthPct: width(amountPerDay),
      }];
    })
    : [];
  const captured = seed.issues.flatMap((issue) => {
    const share = issue.sharedPathContribution;
    if (!share || !issue.resolvedAt || !driverIssueResolvedAtCursor(issue, cursor)) return [];
    const ownerIssue = seed.issues.find((row) => row.id === share.ownerIssueId);
    const at = issue.resolvedAt.slice(11, 16);
    return [{
      issueId: issue.id,
      label: `${issue.shortLabel} · captured ${at}`,
      amountPerDay: share.impactPerDay ?? 0,
      kind: 'captured' as const,
      widthPct: null,
      at,
      ownerLabel: ownerIssue ? nodeLabel(seed.nodes, ownerIssue.path[2]) : '',
    }];
  });
  return { totalPerDay: total, counted, shares, ownerIssueId: owner?.id ?? null, captured };
}

/** One column of the partition grid: its share of the span and whether it
 *  may shrink below its own label (the owner's share columns may; a lone
 *  counted node keeps its label whole and the owner gives way). */
export type ValuePartitionTrack = { pct: number; fitLabel: boolean };

export type ValuePartitionGrid = {
  tracks: ValuePartitionTrack[];
  /** 1-based grid lines per node: counted nodes on row 1, shares on row 2. */
  counted: { issueId: string; start: number; end: number }[];
  shares: { issueId: string; start: number; end: number }[];
};

/**
 * The counted row and the shares row share one set of columns, so the owner
 * spans exactly its shares' columns at every width: the share columns lead,
 * then one column per other counted node. Widths stay `widthPct` wherever the
 * labels fit; a narrow counted node keeps its label and the owner absorbs it.
 */
export function valuePartitionGrid(partition: ValuePartition): ValuePartitionGrid {
  const tracks: ValuePartitionTrack[] = [];
  const counted: ValuePartitionGrid['counted'] = [];
  const shares: ValuePartitionGrid['shares'] = [];
  for (const node of partition.counted) {
    const start = tracks.length + 1;
    if (node.issueId === partition.ownerIssueId && partition.shares.length > 0) {
      for (const share of partition.shares) {
        shares.push({ issueId: share.issueId, start: tracks.length + 1, end: tracks.length + 2 });
        tracks.push({ pct: share.widthPct ?? 0, fitLabel: false });
      }
    } else {
      tracks.push({ pct: node.widthPct ?? 0, fitLabel: node.issueId !== partition.ownerIssueId });
    }
    counted.push({ issueId: node.issueId, start, end: tracks.length + 1 });
  }
  return { tracks, counted, shares };
}

export type DriverLedgerRow = {
  issueId: string;
  rank: number;
  title: string;
  /** Money projections: the primary figure (`one-off` for the stop pair). */
  figure: string;
  /** The figure's comparison lines, under it. */
  comparison: string[];
  /** The tier cell's note under the TARP chip. */
  note: string;
  /** Proportion bar, % of the top counted issue; null for the stop pair. */
  barPct: number | null;
  barTone: 'ink' | 'soft' | 'ok';
  /** A resolved (captured) row reads its figure muted. */
  muted: boolean;
  resolved: boolean;
  oneOff: boolean;
};

/**
 * One ledger row (comp A `dtLedger`): the figure and its comparison, the tier
 * note and the bar. The owner's bar is ink, a captured share's is state-online,
 * the rest are the soft bar; the rank-1 numeral is the only crimson and the
 * UI draws it. The Operator projection renders none of the money fields.
 */
export function driverLedgerRow(
  issue: DriverIssue,
  seed: PricedDriverTree,
  projection: DriverPersonaProjection,
  cursor: SeriesTime,
  top: DriverIssue = topCountedIssue(seed.issues),
): DriverLedgerRow {
  const exposure = issueExposureView(issue, seed, cursor);
  const resolvedLabel = driverIssueResolutionLabel(issue, cursor);
  const resolved = resolvedLabel !== null;
  const note = renderedImpactNote(issue, seed, projection);
  const owner = issue.sharedPathContribution
    ? seed.issues.find((row) => row.id === issue.sharedPathContribution!.ownerIssueId)
    : undefined;
  const ownerLabel = owner ? nodeLabel(seed.nodes, owner.path[2]) : '';
  const base = { issueId: issue.id, rank: issue.rank, title: issue.title, resolved, muted: resolved };
  if (exposure.stopComparison) {
    const stop = exposure.stopComparison;
    return {
      ...base,
      figure: 'one-off',
      comparison: [
        `${stop.plannedHours} h planned · ${formatExposurePerDay(stop.plannedAmount)}`,
        `${stop.avoidedFailureHours} h avoided · ${formatExposurePerDay(stop.avoidedFailureAmount)}`,
      ],
      note: `planned inspection ${stop.plannedHours} h vs avoided failure ${stop.avoidedFailureHours} h`,
      barPct: null,
      barTone: 'soft',
      oneOff: true,
    };
  }
  let comparison: string[];
  if (exposure.recoverySharePerDay !== undefined) {
    comparison = [`throughput arm · ${formatExposurePerDay(exposure.recoverySharePerDay)} recovery within ${ownerLabel}`];
  } else if (exposure.breakdown.length > 0) {
    comparison = [`owner · ${exposure.breakdown.map((row) => `${row.label} ${formatExposurePerDay(row.amountPerDay)}`).join(' · ')}`];
  } else if (exposure.contributor) {
    comparison = [`within ${ownerLabel}${resolved ? ' · captured' : ''}`];
  } else {
    comparison = secondaryNote(note).split('·').slice(1).map((part) => part.trim()).filter(Boolean);
  }
  const tierNote = resolvedLabel
    ?? (issue.impactType === 'Accruing penalty' ? `${issue.impactType} · watch` : secondaryNote(note) || issue.impactType);
  const barPct = Math.round(proportion(issue, top, seed, cursor) * 100);
  return {
    ...base,
    figure: formatExposurePerDay(exposure.amountPerDay),
    comparison,
    note: tierNote,
    barPct,
    barTone: resolved ? 'ok' : issue.id === top.id ? 'ink' : 'soft',
    oneOff: false,
  };
}

/** The Operator's tier-cell note: the dollar-free note's detail, or its impact type. */
export function operatorLedgerNote(issue: DriverIssue, seed: PricedDriverTree, cursor: SeriesTime): string {
  if (issue.stopComparison) {
    // The stop pair's hours carry no money: the same note the money seats read.
    return `planned inspection ${issue.stopComparison.plannedHours} h vs avoided failure ${issue.stopComparison.avoidedFailureHours} h`;
  }
  return driverIssueResolutionLabel(issue, cursor)
    ?? (secondaryNote(renderedImpactNote(issue, seed, 'operator')) || issue.impactType);
}

/** The selected issue's trend (comp B's expand, ~300 px): an ink series, the
 *  target as a neutral dashed basis and the warning in the warning tone. */
export function issueTrendChart(issue: DriverIssue): {
  series: TrendSeries[];
  categories: string[];
  references: TrendReference[];
  yDomain?: readonly [number, number];
  lastValueLabel: string;
} {
  const view = trendViewModel(issue);
  return {
    categories: view.categories,
    lastValueLabel: view.lastValueLabel,
    yDomain: view.yDomain,
    series: view.series.map((row) => ({ ...row, tone: 'neutral' as const, color: 'var(--ink-primary)', area: false })),
    references: view.references.map((row) => row.label === 'target'
      ? { ...row, tone: 'neutral' as const, color: 'var(--ink-tertiary)' }
      : { ...row, tone: 'warn' as const }),
  };
}
